import React, { useEffect, useState } from "react";
import { Quote, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { TOPPER_QUOTES, quoteOfTheDay } from "../data/topperQuotes";

const FILTERS = ["All", "JEE", "NEET"];

// Rotating "Mindset of Toppers" wall — light (marketing) or dark (portal) variant.
export const TopperMindset = ({ variant = "light", compact = false, exam }) => {
  const [filter, setFilter] = useState(exam || "All");
  const pool = TOPPER_QUOTES.filter((q) => filter === "All" || q.exam.startsWith(filter));
  const [i, setI] = useState(() => Math.floor(Date.now() / 86400000) % pool.length);
  useEffect(() => { setI(Math.floor(Date.now() / 86400000) % Math.max(1, pool.length)); }, [filter, pool.length]);
  useEffect(() => {
    if (compact) return;
    const t = setInterval(() => setI((x) => (x + 1) % pool.length), 8000);
    return () => clearInterval(t);
  }, [pool.length, compact]);
  const q = pool[i] || pool[0];
  if (!q) return null;
  const dark = variant === "dark";
  const shell = dark ? "rk-card rk-glow text-white" : "bg-[#071E54] text-white border border-white/10";
  const meta = `${q.exam} ${q.year} · ${q.rank}${q.score ? ` · ${q.score}` : ""}${q.extra ? ` · ${q.extra}` : ""}`;

  if (compact) {
    return (
      <div data-testid="topper-mindset-compact" className={dark ? "rk-panel p-7 md:p-9 relative overflow-hidden" : `${shell} rounded-2xl p-5 relative overflow-hidden`}>
        <Quote className={`absolute top-6 right-7 w-10 h-10 ${dark ? "text-[color:var(--rk-gold)] opacity-25" : "text-white/20"}`} />
        <p className={`rk-eyebrow flex items-center gap-1.5 ${dark ? "rk-gold" : "text-orange-300"}`}><Sparkles className="w-3 h-3" /> Mindset of toppers · today</p>
        <p data-testid="topper-mindset-quote" className={`mt-3 leading-snug ${dark ? "rk-display rk-text text-2xl md:text-3xl max-w-3xl" : "text-base sm:text-lg font-black"}`}>“{q.quote}”</p>
        <p className={`mt-4 text-xs ${dark ? "rk-text-2" : "font-bold text-slate-300"}`}>— <span className={dark ? "rk-text font-semibold" : ""}>{q.name}</span>, <span className={dark ? "rk-gold" : "text-orange-300"}>{meta}</span></p>
      </div>
    );
  }

  return (
    <section data-testid="topper-mindset" className={`${shell} rounded-[28px] p-6 sm:p-10 relative overflow-hidden shadow-2xl`}>
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-amber-400/15 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-fuchsia-500/20 blur-3xl" />
      <div className="relative grid lg:grid-cols-[1fr_auto] gap-6 items-start">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] font-black uppercase tracking-[0.2em] text-amber-300"><Sparkles className="w-3.5 h-3.5" /> Mindset of Toppers</span>
            <span className="text-[10px] font-bold text-slate-300">{TOPPER_QUOTES.length} AIR-1s & national toppers · in their own words</span>
          </div>
          <div className="mt-6 min-h-[150px]">
            <Quote className="w-8 h-8 text-amber-400" />
            <p key={q.name + i} data-testid="topper-mindset-quote" className="mt-2 text-2xl sm:text-4xl font-black leading-tight tracking-tight animate-in fade-in slide-in-from-bottom-2 duration-500">
              “{q.quote}”
            </p>
            <p className="mt-4 text-sm font-black text-amber-300">— {q.name}</p>
            <p className="text-xs font-bold text-slate-300">{meta}</p>
            <p className="text-[10px] text-slate-500 mt-1">Source: {q.source}</p>
          </div>
        </div>
        <div className="flex lg:flex-col items-center gap-2 lg:items-end">
          <div className="flex gap-1 rounded-full bg-white/10 p-1">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`topper-mindset-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)} className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${filter === f ? "bg-amber-400 text-[#071E54]" : "text-slate-200"}`}>{f}</button>
            ))}
          </div>
          <div className="flex gap-1">
            <button data-testid="topper-mindset-prev" aria-label="Previous quote" onClick={() => setI((x) => (x - 1 + pool.length) % pool.length)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="topper-mindset-next" aria-label="Next quote" onClick={() => setI((x) => (x + 1) % pool.length)} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <p className="text-[10px] font-bold text-slate-400">{i + 1} / {pool.length}</p>
        </div>
      </div>
    </section>
  );
};

export const dailyQuote = quoteOfTheDay;
